import { languages } from "@consts/cconsts";
import { Popover, Transition } from "@headlessui/react";
import {
  ComputerDesktopIcon,
  DocumentCheckIcon,
  FolderIcon,
  FolderOpenIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { Fragment, useState } from "react";

interface DesktopMenuProps {
  scroll: boolean;
}

function classNames(...classes: unknown[]) {
  return classes.filter(Boolean).join(" ");
}

export const DesktopMenu = ({ scroll }: DesktopMenuProps) => {
  const { t, lang } = useTranslation("common");
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string>("okna");

  const productCategories = [
    {
      groups: [
        {
          name: "OKNA PVC",
          subgroups: [
            {
              items: [
                {
                  href: "/pvc/windows/veka/pure-82-2-7456",
                  isNew: true,
                  name: "PURE 82+",
                },
                {
                  href: "/pvc/windows/veka/pure-82-7434",
                  isNew: true,
                  name: "PURE 82",
                },
                {
                  href: "/pvc/windows/veka/softline-82-md-36",
                  name: "SOFTLINE 82 MD",
                },
                {
                  href: "/pvc/windows/veka/softline-76-md-72",
                  name: "SOFTLINE 76 MD",
                },
                {
                  href: "/pvc/windows/veka/perfectline-80",
                  name: "PERFECTLINE",
                },
                {
                  href: "/pvc/windows/veka/perfectline-zaokraglony-606",
                  name: "PERFECTLINE ZAOKRĄGLONY",
                },
              ],
              name: "VEKA",
            },
            {
              items: [
                {
                  href: "/pvc/windows/gealan/linear-geo-7359",
                  isNew: true,
                  name: "LINEAR GEO",
                },
                {
                  href: "/pvc/windows/gealan/gealan-kontur-7008",
                  name: "GEALAN KONTUR",
                },
                {
                  href: "/pvc/windows/gealan/gealan-acrylcolor-4674",
                  name: "GEALAN ACRYLCOLOR",
                },
                {
                  href: "/pvc/windows/gealan/gealan-stv-3847",
                  name: "GEALAN STV",
                },
                { href: "/pvc/windows/gealan/linear-307", name: "LINEAR" },
                {
                  href: "/pvc/windows/gealan/optiline-304",
                  name: "OPTILINE",
                },
                {
                  href: "/pvc/windows/gealan/optiline-plus-301",
                  name: "OPTILINE PLUS",
                },
                {
                  href: "/pvc/windows/gealan/termoline-55",
                  name: "TERMOLINE",
                },
              ],
              name: "GEALAN",
            },
            {
              items: [
                {
                  href: "/pvc/windows/decco/decco-70-ad-6954",
                  name: "DECCO 70 AD",
                },
                {
                  href: "/pvc/windows/decco/decco-84-md-6949",
                  name: "DECCO 84 MD",
                },
              ],
              name: "DECCO",
            },
            {
              items: [
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76-flexa-7336",
                  isNew: true,
                  name: "IDEAL NEO 76 FLEXA",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-8000-7046",
                  name: "IDEAL 8000",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-4000-6419",
                  name: "IDEAL 4000",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76ad-6041",
                  name: "IDEAL NEO 76 AD",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76md-6036",
                  name: "IDEAL NEO 76 MD",
                },
                {
                  href: "/pvc/windows/aluplast/intertec-85md-6029",
                  name: "INTERTEC 85 MD",
                },
              ],
              name: "ALUPLAST",
            },
          ],
        },
        {
          name: "OKNA ALU",
          subgroups: [
            ...(lang === "pl"
              ? [
                  {
                    items: [
                      {
                        href: "/alu/windows/aluplast/one-75hi-7614",
                        isNew: true,
                        name: "ONE 75HI",
                      },
                    ],
                    name: "ALUPLAST",
                  },
                ]
              : []),
            {
              items: [
                {
                  href: "/alu/windows/aluprof/mb-104-passive-816",
                  name: "MB 104 PASSIVE",
                },
                { href: "/alu/windows/aluprof/mb-45-716", name: "MB 45" },
                { href: "/alu/windows/aluprof/mb-60-775", name: "MB 60" },
                { href: "/alu/windows/aluprof/mb-70-266", name: "MB 70" },
                { href: "/alu/windows/aluprof/mb-79n-806", name: "MB 79N" },
                {
                  href: "/alu/windows/aluprof/mb-86ei-819",
                  name: "MB 86EI",
                },
                { href: "/alu/windows/aluprof/mb-86n-813", name: "MB 86N" },
                {
                  href: "/alu/windows/aluprof/mb-ferroline-825",
                  name: "MB FERROLINE",
                },
                {
                  href: "/alu/windows/aluprof/mb-slimline-822",
                  name: "MB SLIMLINE",
                },
              ],
              name: "ALUPROF",
            },
            {
              items: [
                { href: "/alu/windows/aluron/as-75-834", name: "AS 75" },
                {
                  href: "/alu/windows/aluron/as-80us-841",
                  name: "AS 80US",
                },
                { href: "/alu/windows/aluron/as-110-838", name: "AS 110" },
              ],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/windows/reynaers/masterline-8-828",
                  name: "MASTERLINE 8",
                },
                {
                  href: "/alu/windows/reynaers/masterline-10-831",
                  name: "MASTERLINE 10",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "okna",
      name: t("pvc.windows").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            {
              href: "/pvc/doors/drzwi-wejsciowe-313",
              name: "DRZWI WEJŚCIOWE",
            },
            {
              href: "/pvc/doors/drzwi-nakladkowe-311",
              name: "DRZWI NAKŁADKOWE",
            },
            {
              href: "/pvc/doors/drzwi-werandowe-309",
              name: "DRZWI WERANDOWE",
            },
          ],
          name: "DRZWI PVC",
        },
        {
          name: "DRZWI ALU",
          subgroups: [
            {
              items: [
                {
                  href: "/alu/doors/aluprof/mb-104-1144",
                  name: "MB 104 PASSIVE AERO",
                },
                { href: "/alu/doors/aluprof/mb-45-1164", name: "MB 45" },
                { href: "/alu/doors/aluprof/mb-60-1161", name: "MB 60" },
                {
                  href: "/alu/doors/aluprof/mb-60e-ei-1141",
                  name: "MB 60E EI",
                },
                { href: "/alu/doors/aluprof/mb-70-1158", name: "MB 70 HI" },
                { href: "/alu/doors/aluprof/mb-78ei-1137", name: "MB 78EI" },
                { href: "/alu/doors/aluprof/mb-79n-1154", name: "MB 79N" },
                { href: "/alu/doors/aluprof/mb-86n-1147", name: "MB 86N" },
                { href: "/alu/doors/aluprof/mb-86st-1150", name: "MB 86ST" },
                { href: "/alu/doors/aluprof/mb-86ei-1133", name: "MB-86EI" },
              ],
              name: "ALUPROF",
            },
            {
              items: [{ href: "/alu/doors/aluron/as-75-1125", name: "AS 75G" }],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/doors/reynaers/masterline-8-1131",
                  name: "MASTERLINE 8",
                },
                {
                  href: "/alu/doors/reynaers/masterline-8-pivot-1127",
                  name: "MASTERLINE 8 PIVOT",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "drzwi",
      name: t("pvc.doors").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            { href: "/alu/multilevel/multieco/me-000-1573", name: "ME 000" },
            { href: "/alu/multilevel/multieco/me-001-1598", name: "ME 001" },
            {
              href: "/alu/multilevel/multieco/me-001nx-1601",
              name: "ME 001NX",
            },
            { href: "/alu/multilevel/multieco/me-002-1604", name: "ME 002" },
            { href: "/alu/multilevel/multieco/me-003-1607", name: "ME 003" },
            {
              href: "/alu/multilevel/multieco/me-003nx-1610",
              name: "ME 003NX",
            },
            { href: "/alu/multilevel/multieco/me-004-1613", name: "ME 004" },
            {
              href: "/alu/multilevel/multieco/me-004nx-1616",
              name: "ME 004NX",
            },
            { href: "/alu/multilevel/multieco/me-008-1619", name: "ME 008" },
            {
              href: "/alu/multilevel/multieco/me-008nx-1622",
              name: "ME 008NX",
            },
            { href: "/alu/multilevel/multieco/me-011-1625", name: "ME 011" },
            {
              href: "/alu/multilevel/multieco/me-011nx-1628",
              name: "ME 011NX",
            },
            { href: "/alu/multilevel/multieco/me-021-1631", name: "ME 021" },
            {
              href: "/alu/multilevel/multieco/me-021nx-1636",
              name: "ME 021NX",
            },
            { href: "/alu/multilevel/multieco/me-211-1643", name: "ME 211" },
            {
              href: "/alu/multilevel/multieco/me-211nx-1650",
              name: "ME 211NX",
            },
          ],
          name: "MULTIECO",
        },
        {
          items: [
            {
              href: "/alu/multilevel/multilevel",
              name: "KOMFORT NA WEJŚCIU (KN 001)",
            },
            {
              href: "/alu/multilevel/multilevel",
              name: "RETRO LINE (KN 038)",
            },
            { href: "/alu/multilevel/multilevel", name: "STONE (KN 0101)" },
            { href: "/alu/multilevel/multilevel", name: "BETON (KN 0300)" },
            { href: "/alu/multilevel/multilevel", name: "LOFT (KN 0400)" },
            { href: "/alu/multilevel/multilevel", name: "NATURAL (KN 0600)" },
          ],
          name: "MULTILEVEL",
        },
        {
          items: [
            {
              href: "/alu/multilevel/technology/ihorizon-exclusive-5226",
              name: "IHORIZON EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/ihorizon-exclusive-plus-5214",
              name: "IHORIZON EXCLUSIVE+",
            },
            {
              href: "/alu/multilevel/technology/mb-79n-exclusive-5217",
              name: "MB-79N EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/mb-86n-exclusive-5221",
              name: "MB-86N EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/mb-86n-exclusive-plus-5207",
              name: "MB-86N EXCLUSIVE+",
            },
            {
              href: "/alu/multilevel/technology/multieco-2-1187",
              name: "MULTIECO",
            },
          ],
          name: "TECHNOLOGY",
        },
      ],
      id: "multilevel",
      name: "MULTILEVEL",
    },
    {
      groups: [
        {
          name: "DRZWI PRZESUWNE PVC",
          subgroups: [
            {
              items: [
                {
                  href: "/pvc/sliding-doors/veka/vekamove-76-5106",
                  name: "VEKAMOVE 76",
                },
                {
                  href: "/pvc/sliding-doors/veka/vekamotion-82-476",
                  name: "VEKAMOTION 82",
                },
              ],
              name: "VEKA",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/gealan/hst-gealan-5108",
                  name: "HST GEALAN",
                },
                {
                  href: "/pvc/sliding-doors/gealan/hs-inowa-480",
                  name: "HS INOWA",
                },
              ],
              name: "GEALAN",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/decco/decco-hst-100-472",
                  name: "DECCO HST 100",
                },
              ],
              name: "DECCO",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/aluplast/smart-slide-neo-6659",
                  name: "SMART-SLIDE NEO",
                },
                {
                  href: "/pvc/sliding-doors/aluplast/hst-85-6656",
                  name: "HST 85",
                },
              ],
              name: "ALUPLAST",
            },
          ],
        },
        {
          name: "DRZWI PRZESUWNE ALU",
          subgroups: [
            {
              items: [
                {
                  href: "/alu/sliding-doors/aluprof/mb-59-galandage-865",
                  name: "MB 59 GALANDAGE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-59-slide-861",
                  name: "MB 59 SLIDE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-59hs-856",
                  name: "MB 59HS",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-77hs-868",
                  name: "MB 77HS",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-86-foldline-878",
                  name: "MB 86 FOLDLINE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-skyline-type-r-873",
                  name: "MB SKYLINE TYPE R",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-slide-854",
                  name: "MB SLIDE",
                },
              ],
              name: "ALUPROF",
            },
            {
              items: [
                {
                  href: "/alu/sliding-doors/aluron/aluron-as-178hs-891",
                  name: "AS 178HS",
                },
                {
                  href: "/alu/sliding-doors/aluron/as-178hs-modern-7427",
                  name: "AS 178HS MODERN",
                },
              ],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/sliding-doors/reynaers/masterpatio-881",
                  name: "MASTERPATIO",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "systemy",
      name: t("pvc.sliding-doors").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/adaptive",
              name: t("rollers.adaptive").toUpperCase(),
            },
            {
              href: "/rollers/concealed",
              name: t("rollers.concealed").toUpperCase(),
            },
            {
              href: "/rollers/for-windows",
              name: t("rollers.for-windows").toUpperCase(),
            },
            {
              href: "/rollers/lamella",
              name: t("rollers.lamella").toUpperCase(),
            },
          ],
          name: t("rollers.title").toUpperCase(),
        },
      ],
      id: "rolety",
      name: t("rollers.title").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/blinds",
              name: t("rollers.blinds").toUpperCase(),
            },
          ],
          name: "ŻALUZJE",
        },
      ],
      id: "zaluzje",
      name: "ŻALUZJE FASADOWE",
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/screens",
              name: t("rollers.screens").toUpperCase(),
            },
          ],
          name: "MOSKITIERY",
        },
      ],
      id: "moskitiery",
      name: "MOSKITIERY",
    },
    {
      groups: [
        {
          items: [
            {
              href: "/alu/facade-systems",
              name: t("alu.facadeSystems").toUpperCase(),
            },
          ],
          name: "FASADY",
        },
      ],
      id: "fasady",
      name: "FASADY",
    },
    {
      groups: [
        {
          items: [
            { href: "/pvc/accessories", name: "AKCESORIA PVC" },
            { href: "/alu/accessories", name: "AKCESORIA ALU" },
          ],
          name: "POZOSTAŁE",
        },
      ],
      id: "dodatki",
      name: "DODATKI",
    },
    {
      groups: [
        {
          items: [
            { href: "/pergolas/mb-opensky-140-3903", name: "MB-OpenSky 140" },
            {
              href: "/pergolas/mb-opensky-120-nowosc-3905",
              name: "MB-OpenSky 120",
            },
          ],
          name: t("pergolas.title").toUpperCase(),
        },
      ],
      id: "pergola",
      name: t("pergolas.title").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            { href: "/partners", name: t("menu.partners").toUpperCase() },
            { href: "/documents", name: t("menu.downloads").toUpperCase() },
          ],
          name: "DLA PARTNERÓW",
        },
      ],
      id: "materialy",
      name: "DOKUMENTY",
    },
  ];

  const currentCategoryData = productCategories.find(
    (c) => c.id === hoveredCategory
  );

  const handleToggleProductsMenu = () =>
    setActiveMenu(activeMenu === "produkty" ? null : "produkty");

  const handleCloseMenu = () => setActiveMenu(null);

  return (
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 lg:px-8">
      {/* Logo */}
      <div className="hidden flex-1 justify-start md:flex">
        <Link className="py-1" href="/">
          <Image
            alt="KNS"
            className={classNames(
              "w-auto transition-all duration-300",
              scroll ? "h-10" : "h-12 lg:h-16"
            )}
            height={80}
            src="/logo/kns-gold-pl.png"
            width={102}
          />
        </Link>
      </div>

      {/* Nav Middle */}
      <nav className="relative z-[60] hidden md:flex md:space-x-8">
        <button
          className={classNames(
            "group relative flex items-center space-x-1 py-5 font-semibold text-sm tracking-wide transition-colors",
            activeMenu === "produkty"
              ? "text-[#c1a166]"
              : "text-white hover:text-[#c1a166]"
          )}
          onClick={handleToggleProductsMenu}
          type="button"
        >
          <span>{t("menu.products").toUpperCase()}</span>
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 transition-transform",
              activeMenu === "produkty" ? "rotate-180" : ""
            )}
          />
          <div
            className={classNames(
              "absolute bottom-3 left-0 h-0.5 bg-[#c1a166] transition-all duration-300",
              activeMenu === "produkty" ? "w-full" : "w-0 group-hover:w-full"
            )}
          />
        </button>

        {[
          { href: "/about-us", name: t("menu.about") },
          { href: "/gallery", name: t("menu.gallery") },
        ].map((item) => (
          <Link
            className="group relative py-5 font-semibold text-sm text-white tracking-wide transition-colors hover:text-[#c1a166]"
            href={item.href}
            key={item.name}
          >
            {item.name.toUpperCase()}
            <div className="absolute bottom-3 left-0 h-0.5 w-0 bg-[#c1a166] transition-all duration-300 group-hover:w-full" />
          </Link>
        ))}

        {/* Pobierz dropdown */}
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button
                className={classNames(
                  "group relative flex items-center space-x-1 py-5 font-semibold text-sm tracking-wide transition-colors",
                  open ? "text-[#c1a166]" : "text-white hover:text-[#c1a166]"
                )}
              >
                <span>{t("menu.downloads").toUpperCase()}</span>
                <ChevronDownIcon
                  className={classNames(
                    "h-4 w-4 transition-transform",
                    open ? "rotate-180" : ""
                  )}
                />
                <div
                  className={classNames(
                    "absolute bottom-3 left-0 h-0.5 bg-[#c1a166] transition-all duration-300",
                    open ? "w-full" : "w-0 group-hover:w-full"
                  )}
                />
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <Popover.Panel className="absolute left-0 z-50 mt-4 w-80 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5">
                  <div className="p-2">
                    {[
                      {
                        description: t("downloads.certificatesDesc"),
                        href: "/certificates",
                        icon: DocumentCheckIcon,
                        name: t("downloads.certificates"),
                      },
                      {
                        description: t("downloads.programDesc"),
                        href: "/application",
                        icon: ComputerDesktopIcon,
                        name: t("downloads.program"),
                      },
                      {
                        description: t("downloads.documentsDesc"),
                        href: "/documents",
                        icon: FolderIcon,
                        name: t("downloads.documents"),
                      },
                      {
                        description: t("downloads.advertisingDesc"),
                        href: "/advertising-materials",
                        icon: FolderOpenIcon,
                        name: t("downloads.advertising"),
                      },
                    ].map((item) => (
                      <Link
                        className="flex items-start space-x-4 rounded-lg p-3 transition-colors hover:bg-gray-50"
                        href={item.href}
                        key={item.name}
                      >
                        <item.icon className="mt-0.5 h-6 w-6 shrink-0 text-[#c1a166]" />
                        <div>
                          <p className="font-bold text-gray-900 text-sm">
                            {item.name}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {item.description}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>

        {[
          { href: "/news", name: t("menu.news") },
          { href: "/contact", name: t("menu.contact") },
        ].map((item) => (
          <Link
            className="group relative py-5 font-semibold text-sm text-white tracking-wide transition-colors hover:text-[#c1a166]"
            href={item.href}
            key={item.name}
          >
            {item.name.toUpperCase()}
            <div className="absolute bottom-3 left-0 h-0.5 w-0 bg-[#c1a166] transition-all duration-300 group-hover:w-full" />
          </Link>
        ))}
      </nav>

      {/* Nav Right (Language) */}
      <div className="hidden flex-1 items-center justify-end md:flex">
        <Popover className="relative">
          {({ open }) => (
            <>
              <Popover.Button className="flex items-center space-x-2 text-white transition-colors hover:text-[#c1a166]">
                <Image
                  alt={lang}
                  className="rounded-full shadow-lg"
                  height={20}
                  src={`/img/locales/${lang}.svg`}
                  width={20}
                />
                <span className="font-bold text-xs uppercase">{lang}</span>
                <ChevronDownIcon
                  className={classNames(
                    "h-4 w-4 transition-transform",
                    open ? "rotate-180" : ""
                  )}
                />
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <Popover.Panel className="absolute right-0 z-50 mt-4 w-48 overflow-hidden rounded-xl bg-[#0a1a33] shadow-2xl ring-1 ring-white/10">
                  <div className="p-2">
                    {languages.map((item) => (
                      <Link
                        className={classNames(
                          "flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition-colors",
                          item.locale === lang
                            ? "bg-[#c1a166] text-white"
                            : "text-white/70 hover:bg-white/5 hover:text-white"
                        )}
                        href={item.href}
                        key={item.locale}
                      >
                        <Image
                          alt={item.locale}
                          className="rounded-full"
                          height={18}
                          src={`/img/locales/${item.locale}.svg`}
                          width={18}
                        />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>

      {/* Mega Menu Overlay */}
      <AnimatePresence>
        {activeMenu === "produkty" && (
          <>
            {/* Backdrop */}
            <motion.div
              animate={{ opacity: 1 }}
              className="fixed inset-0 top-[inherit] z-40 bg-black/60 backdrop-blur-sm"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              onClick={handleCloseMenu}
            />

            {/* Content */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-full right-0 left-0 z-50 flex max-h-[calc(100vh-6rem)] w-full overflow-hidden border-white/10 border-t bg-[#051124] text-white shadow-2xl"
              exit={{ opacity: 0, y: -20 }}
              initial={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="mx-auto flex w-full max-w-7xl">
                {/* Sidebar */}
                <div className="w-1/4 overflow-y-auto border-white/5 border-r py-8">
                  <div className="flex flex-col">
                    {productCategories.map((cat) => {
                      const handleMouseEnter = () => setHoveredCategory(cat.id);
                      return (
                        <button
                          className={classNames(
                            "relative px-8 py-4 text-left font-bold text-sm tracking-widest transition-all",
                            hoveredCategory === cat.id
                              ? "bg-white/5 text-[#c1a166]"
                              : "text-white/60 hover:bg-white/5 hover:text-white"
                          )}
                          key={cat.id}
                          onMouseEnter={handleMouseEnter}
                          type="button"
                        >
                          {cat.name}
                          {hoveredCategory === cat.id && (
                            <motion.div
                              className="absolute top-1/2 left-0 h-6 w-1 -translate-y-1/2 bg-[#c1a166]"
                              layoutId="activeCategorySide"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-10">
                  <div
                    className={classNames(
                      "grid gap-12",
                      currentCategoryData?.groups.some((g) => "subgroups" in g)
                        ? "grid-cols-2"
                        : "grid-cols-2 lg:grid-cols-3"
                    )}
                  >
                    {currentCategoryData?.groups.map((group) => (
                      <div key={group.name}>
                        <h3 className="mb-6 inline-block border-[#c1a166] border-b-2 pb-2 font-bold text-[#c1a166] text-xs tracking-widest">
                          {group.name}
                        </h3>
                        {"subgroups" in group && group.subgroups ? (
                          <div className="space-y-6">
                            {group.subgroups.map((sub) => (
                              <div key={sub.name}>
                                <h4 className="mb-2 border-white/10 border-b pb-1.5 font-bold text-[10px] text-white/40 uppercase tracking-widest">
                                  {sub.name}
                                </h4>
                                <ul className="space-y-2">
                                  {sub.items.map((item) => (
                                    <li key={item.name}>
                                      <Link
                                        className="group flex items-center space-x-2 text-sm text-white/70 transition-colors hover:text-white"
                                        href={item.href}
                                        onClick={handleCloseMenu}
                                      >
                                        <span>{item.name}</span>
                                        {(item as { isNew?: boolean })
                                          .isNew && (
                                          <span className="rounded bg-[#c1a166] px-1.5 py-0.5 font-black text-[#051124] text-[9px]">
                                            NEW
                                          </span>
                                        )}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <ul className="space-y-3">
                            {"items" in group &&
                              group.items?.map((item) => (
                                <li key={item.name}>
                                  <Link
                                    className="group flex items-center space-x-2 text-sm text-white/70 transition-colors hover:text-white"
                                    href={item.href}
                                    onClick={handleCloseMenu}
                                  >
                                    <span
                                      className={classNames(
                                        activeMenu === "produkty"
                                          ? "opacity-100"
                                          : "opacity-0",
                                        "transition-opacity duration-300"
                                      )}
                                    >
                                      {item.name}
                                    </span>
                                    {(item as { isNew?: boolean }).isNew && (
                                      <span className="rounded bg-[#c1a166] px-1.5 py-0.5 font-black text-[#051124] text-[9px]">
                                        NEW
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    ))}
                    {(!currentCategoryData?.groups ||
                      currentCategoryData.groups.length === 0) && (
                      <div className="col-span-full py-20 text-center">
                        <p className="text-white/30 italic">
                          {t("common.comingSoon") || "Wkrótce w ofercie..."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
